import "./App.css";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import RegisterPage from "./pages/auth/RegisterPage";
import LoginPage from "./pages/auth/LoginPage";
import SubscriberRoute from "./route/SubscriberRoute";
import GuestRoute from "./route/GuestRoute";
import { UserProvider } from "./context/UserContext";
import PaymentPlanPage from "./pages/payment/PaymentPlanPage";
import PaymentFormPage from "./pages/payment/PaymentFormPage";
import AnalysisPage from "./pages/AnalysisPage";
import FreeUserRoute from "./route/FreeUserRoute";
import PaymentPlanRoute from "./route/PaymentPlanRoute";
import ProfilePage from "./pages/profile/ProfilePage";
import QueryHistoryPage from "./pages/QueryHistoryPage";
import { useEffect } from "react";
import SendEmailPage from "./pages/auth/SendEmailPage";
import AccountSettingsPage from "./pages/profile/AccountSettingsPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import SentEmailConfirmationPage from "./pages/auth/SentEmailConfirmationPage";
import SuccessForgetPasswordPage from "./pages/auth/SuccessForgetPasswordPage";
import TokenExpiredPage from "./pages/auth/TokenExpiredPage";
import ContactFormPage from "./pages/ContactFormPage";
import UserRoute from "./route/UserRoute";
import PaymentCancelledPage from "./pages/payment/PaymentCancelledPage";
import SubscriptionPlanPage from "./pages/profile/SubscriptionPlanPage";
import PaymentSuccessfulPage from "./pages/payment/PaymentSuccessfulPage";
import QueryDetailsPage from "./pages/QueryDetailsPage";
import BookmarkQueryPage from "./pages/BookmarkQueryPage";
import UnverifiedUserRoute from "./route/UnverifiedUserRoute";
import VerifyEmailRequestPage from "./pages/auth/VerifyEmailRequestPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import TokenEmailVerificationExpiredPage from "./pages/auth/TokenEmailVerificationExpiredPage";
import ResendEmailVerificationPage from "./pages/auth/ResendEmailVerificationPage";
import SentimentConsoleAnalysisPage from "./pages/SentimentConsoleAnalysisPage";

/**
 * Admin
 */
import AdminRoute from "./route/AdminRoute";
import AdminDashboardLayout from "./pages/admin/AdminDashboardLayout";
import DashboardOverviewPage from "./pages/admin/DashboardOverviewPage";
import UserManagementPage from "./pages/admin/UserManagementPage";
import SubscriptionTypesPage from "./pages/admin/SubscriptionTypesPage";
import SubscriptionManagementPage from "./pages/admin/SubscriptionManagementPage";
import UserActivitiesPage from "./pages/admin/UserActivitiesPage";

const router = createBrowserRouter([
  // Admin Routes
  {
    path: "/dashboard",
    element: (
      <AdminRoute>
        <AdminDashboardLayout />
      </AdminRoute>
    ),
    children: [
      {
        index: true,
        element: <DashboardOverviewPage />,
      },
      {
        path: 'user-management',
        element: <UserManagementPage />,
        handle: { title: 'User Management' }
      },
      {
        path: 'subscription-types',
        element: <SubscriptionTypesPage />,
        handle: { title: 'Subscription Types' }
      },
      {
        path: 'subscription-management',
        element: <SubscriptionManagementPage />,
        handle: { title: 'Subscription Management' }
      },
      {
        path: 'user-activities',
        element: <UserActivitiesPage />,
        handle: { title: 'User Activities' }
      },
    ],
  },

  // User Routes
  {
    path: "/profile-page",
    element: (
      <UserRoute>
        <ProfilePage />
      </UserRoute>
    ),
  },
  {
    path: "/account-settings",
    element: (
      <UserRoute>
        <AccountSettingsPage />
      </UserRoute>
    ),
  },

  // Subscriber Routes
  {
    path: "/",
    element: (
      <SubscriberRoute>
        <AnalysisPage />
      </SubscriberRoute>
    )
  },
  {
    path: '/sentiment-analysis',
    element: (
      <SubscriberRoute>
        <SentimentConsoleAnalysisPage />
      </SubscriberRoute>
    )
  },
  {
    path: "/bookmarked-queries",
    element: (
      <SubscriberRoute>
        <BookmarkQueryPage />
      </SubscriberRoute>
    ),
  },
  {
    path: "/query-history",
    element: (
      <SubscriberRoute>
        <QueryHistoryPage />
      </SubscriberRoute>
    ),
  },
  {
    path: "/query-details/:query_id",
    element: (
      <SubscriberRoute>
        <QueryDetailsPage />
      </SubscriberRoute>
    ),
  },
  {
    path: "/payment-successful",
    element: (
      <SubscriberRoute>
        <PaymentSuccessfulPage />
      </SubscriberRoute>
    ),
  },

  // Payment Plan Routes (free users and subscribers)
  {
    path: "/payment-plan",
    element: (
      <PaymentPlanRoute>
        <PaymentPlanPage />
      </PaymentPlanRoute>
    ),
  },
  {
    path: "/subscription-plan",
    element: (
      <PaymentPlanRoute>
        <SubscriptionPlanPage />
      </PaymentPlanRoute>
    ),
  },

  // Free User Routes
  {
    path: "/payment-cancelled",
    element: (
      <FreeUserRoute>
        <PaymentCancelledPage />
      </FreeUserRoute>
    ),
  },
  // IGNORE THIS, I DON'T KNOW WHAT IS ITS ACTUAL FUNCTION
  // Remember the wise word, if you don't know, don't touch it
  {
    path: "/payment-request",
    element: (
      <FreeUserRoute>
        <PaymentFormPage />
      </FreeUserRoute>
    ),
  },

  // Unverified User Routes
  {
    path: "/verify-account",
    element: (
      <UnverifiedUserRoute>
        <VerifyEmailRequestPage />
      </UnverifiedUserRoute>
    ),
  },
  {
    path: "/email-verification/:token",
    element: (
      <UnverifiedUserRoute>
        <VerifyEmailPage />
      </UnverifiedUserRoute>
    ),
  },
  {
    path: "/resend-email-verification",
    element: (
      <UnverifiedUserRoute>
        <ResendEmailVerificationPage />
      </UnverifiedUserRoute>
    ),
  },

  // Guest Routes
  {
    path: "/register",
    element: (
      <GuestRoute>
        <RegisterPage />
      </GuestRoute>
    ),
  },
  {
    path: "/request-reset-password",
    element: (
      <GuestRoute>
        <SendEmailPage />
      </GuestRoute>
    ),
  },
  {
    path: "/sent-email",
    element: (
      <GuestRoute>
        <SentEmailConfirmationPage />
      </GuestRoute>
    ),
  },
  {
    path: "/reset-password/:token",
    element: (
      <GuestRoute>
        <ResetPasswordPage />
      </GuestRoute>
    ),
  },
  {
    path: "/password-reset-confirmation",
    element: (
      <GuestRoute>
        <SuccessForgetPasswordPage />
      </GuestRoute>
    ),
  },
  {
    path: "/login",
    element: (
      <GuestRoute>
        <LoginPage />
      </GuestRoute>
    ),
  },

  // General Routes
  {
    path: "/contact-us",
    element: <ContactFormPage />,
  },
  {
    path: "/token-expired",
    element: <TokenExpiredPage />,
  },
  {
    path: "/token-email-verification-expired",
    element: <TokenEmailVerificationExpiredPage />,
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

const App = () => {
  useEffect(() => {
    document.title = "Console-D";
  }, []);
  return (
    <UserProvider>
      <RouterProvider router={router} />
    </UserProvider>
  );
};

export default App;
